import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw, Play, Zap, MousePointer2, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const ParticleGame = ({ id }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [gameState, setGameState] = useState("start"); // start, playing, gameOver
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const requestRef = useRef();
  
  // Game Refs
  const mousePos = useRef({ x: 0, y: 0 });
  const particles = useRef([]);
  const goals = useRef([]);
  const frameCount = useRef(0);
  const ripple = useRef({ x: 0, y: 0, r: 0, active: false });

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // Refs for dynamic dimensions
  const canvasWidth = useRef(800);
  const canvasHeight = useRef(500);

  // Constants
  const PARTICLE_COUNT = 40;

  useEffect(() => {
    const saved = localStorage.getItem("meraki_particle_high_score");
    if (saved) setHighScore(parseInt(saved));

    const checkTouch = () => {
      setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia("(pointer: coarse)").matches);
    };

    const updateCanvasSize = () => {
      if (containerRef.current && canvasRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        canvasWidth.current = clientWidth * window.devicePixelRatio;
        canvasHeight.current = clientHeight * window.devicePixelRatio;
        canvasRef.current.width = canvasWidth.current;
        canvasRef.current.height = canvasHeight.current;
      }
    };

    // Force non-passive touchmove listener to prevent scrolling
    const container = containerRef.current;
    const preventDefault = (e) => {
      if (container && container.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (container) {
      container.addEventListener('touchmove', preventDefault, { passive: false });
    }

    checkTouch();
    updateCanvasSize();
    window.addEventListener('resize', () => {
      checkTouch();
      updateCanvasSize();
    });
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      if (container) {
        container.removeEventListener('touchmove', preventDefault);
      }
    };
  }, []);

  const initGame = () => {
    particles.current = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.current.push({
        x: Math.random() * canvasWidth.current,
        y: Math.random() * canvasHeight.current,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        radius: Math.random() * 3 + 2,
        color: "#e4c77a"
      });
    }
    
    // Initial goals
    spawnGoal();
    setScore(0);
    setGameState("playing");
    frameCount.current = 0;
  };

  const spawnGoal = () => {
    goals.current = [{
      x: Math.random() * (canvasWidth.current - 100) + 50,
      y: Math.random() * (canvasHeight.current - 100) + 50,
      radius: 40,
      life: 300, // frames
      id: Date.now()
    }];
  };

  const handleMouseMove = (e) => {
    if (!containerRef.current || isTouchDevice) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) * window.devicePixelRatio;
    const y = (e.clientY - rect.top) * window.devicePixelRatio;
    mousePos.current = { x, y };
  };

  const handleTouchMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = (touch.clientX - rect.left) * window.devicePixelRatio;
    const y = (touch.clientY - rect.top) * window.devicePixelRatio;
    mousePos.current = { x, y };
  };

  const handleClick = () => {
    if (gameState === "playing") {
      ripple.current = { x: mousePos.current.x, y: mousePos.current.y, r: 0, active: true };
    } else if (gameState === "start" || gameState === "gameOver") {
      initGame();
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const update = () => {
      if (gameState !== "playing") return;
      frameCount.current++;

      // Update Particles
      particles.current.forEach(p => {
        // Attract to mouse
        const dx = mousePos.current.x - p.x;
        const dy = mousePos.current.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < 200) {
          const force = (200 - dist) / 2000;
          p.vx += dx * force;
          p.vy += dy * force;
        }

        // Ripple Repel
        if (ripple.current.active) {
          const rdx = ripple.current.x - p.x;
          const rdy = ripple.current.y - p.y;
          const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
          if (rdist < ripple.current.r && rdist > ripple.current.r - 20) {
            p.vx -= (rdx / rdist) * 15;
            p.vy -= (rdy / rdist) * 15;
          }
        }

        // Apply Friction
        p.vx *= 0.96;
        p.vy *= 0.96;
        
        p.x += p.vx;
        p.y += p.vy;

        // Boundaries
        if (p.x < 0 || p.x > canvasWidth.current) p.vx *= -1;
        if (p.y < 0 || p.y > canvasHeight.current) p.vy *= -1;
      });

      // Update Ripple
      if (ripple.current.active) {
        ripple.current.r += 10;
        if (ripple.current.r > 400) ripple.current.active = false;
      }

      // Update Goals
      goals.current.forEach((goal, gIdx) => {
        goal.life--;
        
        // Count particles inside goal
        let insideCount = 0;
        particles.current.forEach(p => {
          const dx = goal.x - p.x;
          const dy = goal.y - p.y;
          if (Math.sqrt(dx * dx + dy * dy) < goal.radius) insideCount++;
        });

        if (insideCount > 5) {
          setScore(s => s + 1);
          goal.radius += 0.5; // Grow as reward
        }

        if (goal.life <= 0) {
          if (score < frameCount.current / 10) {
            // setGameState("gameOver"); // Too slow
          }
          spawnGoal();
        }
      });

      if (frameCount.current > 1800) { // 30 seconds game
        setGameState("gameOver");
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvasWidth.current, canvasHeight.current);
      
      // Draw background
      ctx.fillStyle = "#0a0908";
      ctx.fillRect(0, 0, canvasWidth.current, canvasHeight.current);

      // Draw Goals (Render Zones)
      goals.current.forEach(goal => {
        const grad = ctx.createRadialGradient(goal.x, goal.y, 0, goal.x, goal.y, goal.radius);
        grad.addColorStop(0, "rgba(228, 199, 122, 0.4)");
        grad.addColorStop(1, "transparent");
        
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(goal.x, goal.y, goal.radius, 0, Math.PI * 2);
        ctx.fill();
        
        // Ring
        ctx.strokeStyle = "rgba(228, 199, 122, 0.3)";
        ctx.setLineDash([5, 5]);
        ctx.beginPath();
        ctx.arc(goal.x, goal.y, goal.radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        
        // Timer Arc
        ctx.strokeStyle = "#e4c77a";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(goal.x, goal.y, goal.radius + 5, -Math.PI/2, -Math.PI/2 + (goal.life/300) * Math.PI*2);
        ctx.stroke();
      });

      // Draw Particles
      particles.current.forEach(p => {
        ctx.shadowBlur = 10;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Draw connections
        particles.current.forEach(p2 => {
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 80) {
            ctx.strokeStyle = `rgba(228, 199, 122, ${1 - dist/80})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });

      // Draw Ripple
      if (ripple.current.active) {
        ctx.strokeStyle = "rgba(228, 199, 122, 0.4)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ripple.current.x, ripple.current.y, ripple.current.r, 0, Math.PI * 2);
        ctx.stroke();
      }

      if (gameState === "playing") {
        requestRef.current = requestAnimationFrame(() => {
          update();
          draw();
        });
      } else {
        requestRef.current = requestAnimationFrame(draw);
      }
    };

    requestRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, score]);

  return (
    <section id={id} className="py-24 relative overflow-hidden bg-[var(--ink)]">
      <div className="container-xl px-4 md:px-6">
        <div className="flex flex-col mb-12">
          <div className="text-center md:text-left mb-8">
            <Kicker index="LAB-04" label="Kinetic Playground" />
          </div>
          <div className="text-center">
            <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--cream)]">
              Motion <span className="italic gold-text">Flow</span>
            </h2>
            <p className="mt-4 text-[var(--cream-muted)] text-sm uppercase tracking-[0.2em] max-w-lg mx-auto">
              Attract particles to the render zones. Click to pulse.
            </p>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mt-6 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors mx-auto text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Play
            </button>
          </div>
        </div>

        <div 
          ref={containerRef}
          className="relative max-w-4xl mx-auto aspect-[3/4] md:aspect-[16/9] rounded-[40px] overflow-hidden border border-[var(--rule)] bg-[#050505] shadow-[0_0_100px_rgba(0,0,0,0.5)] group cursor-none touch-none"
          data-lenis-prevent
          onMouseMove={handleMouseMove}
          onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); handleTouchMove(e); }}
          onWheel={(e) => e.stopPropagation()}
          onClick={handleClick}
        >
          <canvas 
            ref={canvasRef}
            width={800}
            height={500}
            className="w-full h-full"
          />

          <AnimatePresence>
            {gameState === "start" && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex flex-col items-center justify-center z-30"
              >
                <div 
                  className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: "url('/particle_game_thumbnail_1778427178690.png')" }}
                />
                <div className="absolute inset-0 z-10 bg-black/70 backdrop-blur-[2px]" />

                <div className="relative z-20 flex flex-col items-center p-8 md:p-12 rounded-3xl border border-[var(--gold)]/20 bg-black/40 backdrop-blur-md max-w-[85%] md:max-w-none">
                  <Sparkles className="text-[var(--gold)] w-12 h-12 md:w-16 md:h-16 mb-6 md:mb-8 animate-pulse" />
                  <h3 className="font-display text-3xl md:text-4xl text-white mb-4 italic text-center leading-tight">Ignite the Motion?</h3>
                  <p className="text-[var(--cream-muted)] text-[8px] md:text-[10px] tracking-[0.3em] uppercase mb-8 md:mb-10 text-center leading-relaxed">
                    {isTouchDevice ? "Drag to attract. Tap to repel." : "Hover to attract. Click to repel."} <br /> Fill the zones to score.
                  </p>
                  <button className="btn-primary px-8 md:px-10 py-4" onClick={(e) => { e.stopPropagation(); initGame(); }}>
                    Enter Flow
                  </button>
                </div>
              </motion.div>
            )}

            {gameState === "gameOver" && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute inset-0 flex flex-col items-center justify-center bg-black/95 backdrop-blur-2xl z-30"
              >
                <Zap className="text-[var(--gold)] w-20 h-20 mb-6" />
                <h3 className="font-display text-5xl text-white mb-2 tracking-tighter uppercase">Render Complete</h3>
                <p className="text-[var(--gold)] font-impact text-sm tracking-[0.4em] uppercase mb-12">Kinetic energy captured</p>
                
                <div className="flex gap-16 mb-12">
                  <div className="text-center">
                    <div className="text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">FLOW SCORE</div>
                    <div className="text-5xl font-display text-white">{score}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[10px] text-[var(--muted)] uppercase tracking-[0.2em] mb-2">MAX FLOW</div>
                    <div className="text-5xl font-display text-[var(--gold)]">{highScore}</div>
                  </div>
                </div>

                <button className="btn-primary px-12" onClick={(e) => { e.stopPropagation(); initGame(); }}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Restart Render
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* HUD */}
          {gameState === "playing" && (
            <div className="absolute top-10 right-10 text-right z-20 pointer-events-none">
              <div className="text-[10px] text-[var(--gold)] uppercase tracking-[0.3em] mb-1">ENERGY LEVEL</div>
              <div className="text-3xl font-display text-white">{score}</div>
            </div>
          )}
          
          {/* Mouse Tracer - Only on desktop */}
          {gameState === "playing" && !isTouchDevice && (
            <div 
              className="absolute w-10 h-10 border border-[var(--gold)] rounded-full pointer-events-none z-40 transition-transform duration-100"
              style={{ 
                left: mousePos.current.x / window.devicePixelRatio, 
                top: mousePos.current.y / window.devicePixelRatio, 
                transform: "translate(-50%, -50%)",
                background: "radial-gradient(circle, rgba(228,199,122,0.2), transparent 70%)"
              }}
            />
          )}
        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Kinetic Flow"
        instructions={[
          { title: "Hover to Attract", desc: "Your cursor acts as a magnet. Move it across the field to draw the golden particles toward you." },
          { title: "Fill the Zones", desc: "Guide particles into the glowing 'Render Zones'. The more particles inside a zone, the higher your energy level." },
          { title: "Pulse Wave", desc: "Click anywhere to trigger a kinetic ripple. This will push particles away from the center, useful for resetting their positions." },
          { title: "Capture Energy", desc: "Keep the flow stable for 30 seconds to complete the render and capture maximum energy." }
        ]}
      />
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center justify-center md:justify-start gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default ParticleGame;
